import { localizeByKeyStatic } from "components/service";
import { formatCommunicationDateTimeShort } from "./communication-date-helper";
import { localizeCommunicationLookupValue } from "./communication-localize-helper";
import type {
  TCommunicationEquipmentChange,
  TCommunicationFinalChanges,
  TCommunicationPreviewLogisticsRow,
  TCommunicationPreviewOrderLine,
  TCommunicationPreviewQuestionLine,
  TCommunicationQuestion,
  TCommunicationTask,
} from "./communication-types-helper";

type TCommunicationBasePreviewSource = Pick<
  TCommunicationTask,
  "event_name" | "company_name" | "stand_number" | "received_at"
>;

type TCommunicationPreviewSource =
  | (TCommunicationBasePreviewSource & Pick<TCommunicationTask, "questions" | "final_changes">)
  | (TCommunicationBasePreviewSource & {
    questions?: TCommunicationQuestion[];
    changes: TCommunicationFinalChanges;
  });

const formatOrderChangeText = (change: TCommunicationEquipmentChange): string => {
  const localizedColor = localizeCommunicationLookupValue(change.color);
  const colorSuffix = localizedColor ? ` (${localizedColor})` : "";

  if (change.action === "remove") {
    return localizeByKeyStatic("COMMUNICATION.Preview_OrderChangeRemove", {
      item: change.item_label,
      qty: change.qty,
      color: colorSuffix,
    });
  }

  if (change.action === "change") {
    return localizeByKeyStatic("COMMUNICATION.Preview_OrderChangeChange", {
      item: change.item_label,
      qty: change.qty,
      color: colorSuffix,
    });
  }

  return localizeByKeyStatic("COMMUNICATION.Preview_OrderChangeAdd", {
    item: change.item_label,
    qty: change.qty,
    color: colorSuffix,
  });
};

const toSignedQuantity = (change: TCommunicationEquipmentChange): number =>
  change.action === "remove" ? -Math.abs(change.qty) : Math.abs(change.qty);

const getPreviewChanges = (task: TCommunicationPreviewSource): TCommunicationFinalChanges =>
  "changes" in task ? task.changes : task.final_changes;

export const buildCommunicationOrderPreview = (
  task: TCommunicationPreviewSource
): TCommunicationPreviewOrderLine[] => {
  const changes = getPreviewChanges(task);
  const orderLines = changes.equipment_changes.map((change) => ({
    id: `order-${change.id}`,
    text: formatOrderChangeText(change),
  }));

  const flooringColor = localizeCommunicationLookupValue(changes.stand_updates.flooring_color);

  if (flooringColor) {
    orderLines.push({
      id: `order-flooring-${task.received_at}`,
      text: localizeByKeyStatic("COMMUNICATION.Preview_OrderChangeFlooring", {
        color: flooringColor,
      }),
    });
  }

  return orderLines;
};

export const buildCommunicationQuestionPreview = (
  task: Pick<TCommunicationPreviewSource, "questions" | "received_at">
): TCommunicationPreviewQuestionLine[] =>
  (task.questions || []).map((question: TCommunicationQuestion) => ({
    id: `question-${question.id}`,
    text: question.text,
    status: question.status,
  }));

export const buildCommunicationLogisticsPreview = (
  task: TCommunicationPreviewSource
): TCommunicationPreviewLogisticsRow[] => {
  const changes = getPreviewChanges(task);

  return changes.equipment_changes.map((change) => ({
    id: `logistics-${change.id}`,
    event_name: task.event_name,
    action: change.action,
    product: change.item_label,
    company_name: task.company_name,
    stand_number: change.stand_number || task.stand_number,
    quantity: toSignedQuantity(change),
    received_at: formatCommunicationDateTimeShort(task.received_at),
  }));
};

export const formatCommunicationPreviewDate = formatCommunicationDateTimeShort;
